// PROTOTYPE
export type VideoEmbedProvider = 'facebook' | 'vimeo' | 'youtube';
export type VideoUploadError = 'format' | 'size';

export interface VideoEmbed {
  embedUrl: string;
  provider: VideoEmbedProvider;
}

export const VIDEO_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
export const VIDEO_UPLOAD_ACCEPT =
  'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm';

// The Java backend's HTML sanitizer allowlists <a href> but not <iframe>, so an embed persists as
// an anchor carrying this URL fragment marker and is upgraded back to an iframe on read.
export const VIDEO_EMBED_MARKER = 'seaters-video';

const VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
]);
const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.webm']);

const YOUTUBE_ID_PATTERN = /^[\w-]{6,20}$/;
const VIMEO_ID_PATTERN = /^\d{6,12}$/;

/**
 * Origin is compared exactly; the path keeps an allowed host from serving anything
 * else. A prefix must end in `/`, or it would also admit `/plugins/video.php.evil/x`.
 */
const ALLOWED_EMBEDS: {
  exactPath?: boolean;
  origin: string;
  pathPrefix: string;
  provider: VideoEmbedProvider;
}[] = [
  { origin: 'https://www.youtube.com', pathPrefix: '/embed/', provider: 'youtube' },
  { origin: 'https://player.vimeo.com', pathPrefix: '/video/', provider: 'vimeo' },
  {
    exactPath: true,
    origin: 'https://www.facebook.com',
    pathPrefix: '/plugins/video.php',
    provider: 'facebook',
  },
];

export function getVideoEmbedProvider(src: string): VideoEmbedProvider | null {
  if (!src) {
    return null;
  }

  let url: URL;

  try {
    url = new globalThis.URL(src);
  } catch {
    return null;
  }

  return (
    ALLOWED_EMBEDS.find(
      (entry) =>
        url.origin === entry.origin &&
        (entry.exactPath
          ? url.pathname === entry.pathPrefix
          : url.pathname.startsWith(entry.pathPrefix))
    )?.provider ?? null
  );
}

/**
 * An uploaded video plays from a `blob:` object URL for the authoring session, or
 * over https. Anything else — `javascript:`, `data:`, plain http — is refused.
 */
export function isPlayableVideoSrc(src: string): boolean {
  if (src.startsWith('blob:')) {
    return true;
  }

  try {
    return new globalThis.URL(src).protocol === 'https:';
  } catch {
    return false;
  }
}

export function isAllowedVideoEmbedSrc(src: string | null | undefined): boolean {
  return getVideoEmbedProvider(src ?? '') !== null;
}

export function toVideoCarrierHref(src: string): string {
  return `${src}#${VIDEO_EMBED_MARKER}`;
}

/** Only a href carrying the marker fragment, over an allowlisted origin/path, is a video carrier. */
export function parseVideoCarrierHref(href: string | null): string | null {
  if (!href) {
    return null;
  }

  let url: URL;

  try {
    url = new globalThis.URL(href);
  } catch {
    return null;
  }

  if (url.hash !== `#${VIDEO_EMBED_MARKER}`) {
    return null;
  }

  url.hash = '';
  const src = url.toString();

  return isAllowedVideoEmbedSrc(src) ? src : null;
}

function stripWww(hostname: string): string {
  return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
}

function buildYoutubeEmbed(id: string): VideoEmbed | null {
  return YOUTUBE_ID_PATTERN.test(id)
    ? { embedUrl: `https://www.youtube.com/embed/${id}`, provider: 'youtube' }
    : null;
}

function buildVimeoEmbed(id: string): VideoEmbed | null {
  return VIMEO_ID_PATTERN.test(id)
    ? { embedUrl: `https://player.vimeo.com/video/${id}`, provider: 'vimeo' }
    : null;
}

function parseYoutubeHostedUrl(url: URL): VideoEmbed | null {
  const { pathname } = url;

  if (pathname === '/watch') {
    return buildYoutubeEmbed(url.searchParams.get('v') ?? '');
  }

  const embedMatch = /^\/embed\/([^/]+)/.exec(pathname);
  if (embedMatch) {
    return buildYoutubeEmbed(embedMatch[1]);
  }

  const shortsMatch = /^\/shorts\/([^/]+)/.exec(pathname);
  if (shortsMatch) {
    return buildYoutubeEmbed(shortsMatch[1]);
  }

  const liveMatch = /^\/live\/([^/]+)/.exec(pathname);
  if (liveMatch) {
    return buildYoutubeEmbed(liveMatch[1]);
  }

  return null;
}

/**
 * Accepts what a human pastes and builds the embed URL from a validated id — never from the raw
 * string. Facebook is the one case the input string survives, as an encoded query param.
 */
export function parseVideoEmbedUrl(input: string): VideoEmbed | null {
  const trimmed = input.trim();

  if (!trimmed) {
    return null;
  }

  let url: URL;

  try {
    url = new globalThis.URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  const host = stripWww(url.hostname.toLowerCase());

  if (host === 'youtu.be') {
    return buildYoutubeEmbed(url.pathname.split('/')[1] ?? '');
  }

  if (
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    return parseYoutubeHostedUrl(url);
  }

  if (host === 'vimeo.com') {
    const segments = url.pathname.split('/').filter(Boolean);
    return buildVimeoEmbed(segments.at(-1) ?? '');
  }

  if (host === 'player.vimeo.com') {
    const match = /^\/video\/(\d+)/.exec(url.pathname);
    return match ? buildVimeoEmbed(match[1]) : null;
  }

  if (
    host === 'facebook.com' ||
    host === 'm.facebook.com' ||
    host === 'web.facebook.com' ||
    host === 'fb.watch'
  ) {
    if (url.protocol !== 'https:') {
      return null;
    }

    return {
      embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url.toString())}&show_text=false`,
      provider: 'facebook',
    };
  }

  return null;
}

function getFileExtension(name: string): string {
  const dotIndex = name.lastIndexOf('.');
  return dotIndex === -1 ? '' : name.slice(dotIndex).toLowerCase();
}

export function validateVideoFile(file: {
  name: string;
  size: number;
  type: string;
}): VideoUploadError | null {
  const hasAllowedFormat = file.type
    ? VIDEO_MIME_TYPES.has(file.type)
    : VIDEO_EXTENSIONS.has(getFileExtension(file.name));

  if (!hasAllowedFormat) {
    return 'format';
  }

  if (file.size > VIDEO_UPLOAD_MAX_BYTES) {
    return 'size';
  }

  return null;
}
